import { InfoOutlined, ExpandMore, ExpandLess } from '@mui/icons-material';
import { RoleRestriction } from '../enums';
import { useState } from 'react';

const ACCENT = '#ff7500';

interface RoleInfo {
  title: string;
  description: string;
}

const ROLES_BY_TYPE: Partial<Record<RoleRestriction, RoleInfo[]>> = {
  [RoleRestriction.COMMERCE]: [
    {
      title: 'Gestión y reportes',
      description:
        'Puede crear, editar, eliminar sus envíos, imprimir etiquetas y además ver reportes particulares.',
    },
    {
      title: 'Gestión',
      description:
        'Puede crear, editar y eliminar sus envíos, e imprimir etiquetas.',
    },
    {
      title: 'Solo lectura',
      description:
        'Solo puede ver sus envíos e imprimir etiquetas. No los modifica.',
    },
  ],
  [RoleRestriction.EXTERNAL_OPERATOR]: [
    {
      title: 'Supervisor',
      description:
        'Puede ver y editar envíos, armar rutas, cambiar estados y además administrar sus comercios, sucursales, servicios, zonas y franjas horarias.',
    },
    {
      title: 'Afiliado',
      description:
        'Puede ver y editar envíos, armar rutas y cambiar estados. No administra comercios, sucursales, servicios, zonas ni franjas horarias.',
    },
    {
      title: 'Independiente',
      description:
        'Puede ver sus envíos, armar rutas e imprimir etiquetas. No puede cambiar estados.',
    },
  ],
  [RoleRestriction.OPERATOR]: [
    {
      title: 'Administrador',
      description:
        'Puede hacer todo: armar rutas, crear usuarios y cambiar configuraciones.',
    },
    {
      title: 'Supervisor',
      description:
        'Gestiona la operación: envíos, comercios y conductores.',
    },
    {
      title: 'Operador',
      description:
        'Trabaja el día a día con los envíos. No entra a Gestión de Rutas.',
    },
    {
      title: 'Monitoreo y creación de rutas',
      description: 'Arma y gestiona las rutas, y monitorea la operación.',
    },
    {
      title: 'Lector',
      description: 'Solo puede mirar, no modifica nada.',
    },
    {
      title: 'Supervisor de pickup externo',
      description:
        'Supervisa los puntos externos que reciben pedidos.',
    },
    {
      title: 'Subagente de pickup externo',
      description:
        'Empleado de un tercero que recibe pedidos en su local.',
    },
    {
      title: 'Espectador de pickup externo',
      description:
        'Solo consulta la información de los puntos externos, sin operar.',
    },
  ],
};

interface InviteRolesInfoProps {
  userType?: string;
}

const InviteRolesInfo = ({ userType }: InviteRolesInfoProps) => {
  const [open, setOpen] = useState(false);
  const roles = userType
    ? ROLES_BY_TYPE[userType as RoleRestriction]
    : undefined;

  if (!roles?.length) return null;

  return (
    <div className='overflow-hidden rounded-xl border border-[#ff750033] bg-[#ff75000a]'>
      <button
        type='button'
        onClick={() => setOpen((prev) => !prev)}
        className='flex w-full cursor-pointer items-center justify-between gap-2 px-4 py-2.5 text-left transition-colors hover:bg-[#ff750014]'
        aria-expanded={open}
      >
        <span className='flex items-center gap-2 text-sm font-semibold text-gray-800'>
          <InfoOutlined sx={{ fontSize: 18, color: ACCENT }} />
          ¿Qué puede hacer cada rol?
        </span>
        {open ? (
          <ExpandLess sx={{ fontSize: 20, color: ACCENT }} />
        ) : (
          <ExpandMore sx={{ fontSize: 20, color: ACCENT }} />
        )}
      </button>

      {open && (
        <div className='grid grid-cols-1 gap-3 border-t border-[#ff750022] bg-white p-4 md:grid-cols-2'>
          {roles.map(({ title, description }) => (
            <div
              key={title}
              className='flex flex-col gap-0.5 rounded-lg border border-gray-100 bg-gray-50/60 p-3'
            >
              <span className='text-sm font-semibold text-gray-900'>
                {title}
              </span>
              <span className='text-xs leading-relaxed text-gray-500'>
                {description}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default InviteRolesInfo;
